import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const faqs = [
  { q: "How does the process work?", a: "We start with a brief call to understand your project needs and client goals. Then I'll provide a detailed proposal. Once approved, we move into production with regular check-ins until you're confident to deploy." },
  { q: "How do you integrate with our agency workflow?", a: "I adapt to your existing processes — whether that's Slack, email, or project management tools. I join your team calls when needed and deliver assets in your preferred formats." },
  { q: "What about tight deadlines?", a: "Agency timelines are built into how I work. Rush delivery is available, and I'm upfront about what's achievable so there are no surprises." },
  { q: "Can you handle multiple projects at once?", a: "Absolutely. I scale capacity to match your pipeline — from single projects to ongoing retainer work across multiple clients." },
  { q: "What are the payment terms?", a: "For project work, 50% upfront to begin with the remainder on completion. Retainer clients enjoy monthly invoicing. We can discuss terms that work for your agency." },
];

function PricingCard({ pkg, index }) {
  const isPopular = pkg.popular;
  return (
    <motion.div
      {...fadeUp}
      transition={{ delay: index * 0.1 }}
      className={`relative flex flex-col h-full rounded-2xl p-8 border ${isPopular ? "bg-[#1C1C1C] text-white border-[#1C1C1C]" : "bg-white text-[#1C1C1C] border-[#E8E5E0] hover:border-[#FF5E3A]/30"} transition-colors`}
      data-testid={`pricing-card-${pkg.id}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-8 bg-[#FF5E3A] text-white px-4 py-1 rounded-full">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Most Popular</span>
        </div>
      )}
      <div className="mb-6">
        <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
        <p className={`text-sm leading-relaxed ${isPopular ? "text-white/50" : "text-[#1C1C1C]/40"}`}>{pkg.description}</p>
      </div>
      <div className="mb-8"><span className="text-3xl font-black">{pkg.price}</span></div>
      <ul className="space-y-3 mb-8 flex-grow">
        {pkg.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm">
            <Check size={16} className="mt-0.5 flex-shrink-0 text-[#FF5E3A]" />
            <span className={isPopular ? "text-white/70" : "text-[#1C1C1C]/50"}>{feature}</span>
          </li>
        ))}
      </ul>
      <Link to="/contact" className="mt-auto">
        <button className={`w-full font-semibold text-sm py-4 rounded-full transition-colors duration-300 ${isPopular ? "bg-[#FF5E3A] text-white hover:bg-white hover:text-[#1C1C1C]" : "border-2 border-[#1C1C1C]/15 text-[#1C1C1C] hover:bg-[#FF5E3A] hover:border-[#FF5E3A] hover:text-white"}`} data-testid={`pricing-cta-${pkg.id}`}>
          Get Started <ArrowRight size={14} className="inline ml-1" />
        </button>
      </Link>
    </motion.div>
  );
}

export default function Prices() {
  const [packages, setPackages] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try { const res = await axios.get(`${API}/pricing`); setPackages(res.data); }
      catch { toast.error("Failed to load pricing"); }
    };
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3]" data-testid="prices-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">§ 06 / Pricing</p>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C] mb-2">
              Simple, transparent <em className="not-italic text-[#FF5E3A]">pricing.</em>
            </h1>
            <div className="w-24 h-1 bg-[#FF5E3A] mt-8 mb-10" />
            <p className="text-base md:text-lg leading-relaxed text-[#1C1C1C]/50">Every project is unique, so these are starting points — let's talk about your specific needs.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => <PricingCard key={pkg.id} pkg={pkg} index={index} />)}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="bg-[#1C1C1C] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Need something <em className="not-italic text-[#FF5E3A]">custom?</em></h2>
              <p className="text-white/50 max-w-xl">Every brand is unique. Let's chat about creating a custom solution.</p>
            </div>
            <Link to="/contact">
              <button className="bg-[#FF5E3A] text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white hover:text-[#1C1C1C] transition-colors duration-300 whitespace-nowrap" data-testid="custom-quote-btn">Request Custom Quote</button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">FAQ</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-[#1C1C1C]">Common <em className="not-italic text-[#FF5E3A]">questions.</em></h2>
          </motion.div>
          <motion.div {...fadeUp} className="border-t border-[#E8E5E0]">
            {faqs.map((faq, index) => (
              <div key={faq.q} className={`border-b border-[#E8E5E0] ${openFaq === faq.q ? "bg-[#F8F6F3]" : ""}`} data-testid={`faq-${index}`}>
                <button className="w-full py-6 px-6 flex items-center justify-between text-left" onClick={() => setOpenFaq(openFaq === faq.q ? null : faq.q)} data-testid={`faq-toggle-${index}`}>
                  <span className="font-semibold text-lg pr-4 text-[#1C1C1C]">{faq.q}</span>
                  <ChevronDown size={20} className={`text-[#FF5E3A] flex-shrink-0 transition-transform ${openFaq === faq.q ? "rotate-180" : ""}`} />
                </button>
                {openFaq === faq.q && <div className="px-6 pb-6"><p className="text-[#1C1C1C]/50 leading-relaxed">{faq.a}</p></div>}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FF5E3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight text-white mb-10">Ready to get started?</h2>
            <Link to="/contact">
              <button className="bg-white text-[#1C1C1C] font-semibold text-sm px-10 py-5 rounded-full hover:bg-[#1C1C1C] hover:text-white transition-colors duration-300 inline-flex items-center gap-2" data-testid="prices-cta-btn">Start a Project <ArrowRight size={14} /></button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
